import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number | null} */
  #stickyStateFrame = null;

  #summaryResizeObserver = new ResizeObserver(() => this.#queueStickyStateUpdate());

  /** @type {Set<Element>} */
  #resizeTargets = new Set();

  #drawerMutationObserver = new MutationObserver(() => this.#queueStickyStateUpdate());

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#queueStickyStateUpdate);

    const { dialog } = /** @type {Refs} */ (this.refs);
    this.#drawerMutationObserver.observe(dialog, {
      attributes: true,
      attributeFilter: ['open'],
      childList: true,
      subtree: true,
    });
    this.#queueStickyStateUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#queueStickyStateUpdate);
    this.#summaryResizeObserver.disconnect();
    this.#resizeTargets.clear();
    this.#drawerMutationObserver.disconnect();

    if (this.#stickyStateFrame !== null) {
      cancelAnimationFrame(this.#stickyStateFrame);
      this.#stickyStateFrame = null;
    }
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #queueStickyStateUpdate = () => {
    if (this.#stickyStateFrame !== null) return;

    this.#stickyStateFrame = requestAnimationFrame(() => {
      this.#stickyStateFrame = null;
      this.#updateStickyState();
    });
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    // Retain subscriptions: disconnecting and observing again delivers another
    // initial resize notification, creating a frame-by-frame update loop.
    const targets = new Set([dialog, ...(summary ? [summary] : [])]);
    for (const target of this.#resizeTargets) {
      if (!targets.has(target)) this.#summaryResizeObserver.unobserve(target);
    }
    for (const target of targets) {
      if (!this.#resizeTargets.has(target)) this.#summaryResizeObserver.observe(target);
    }
    this.#resizeTargets = targets;

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      if (dialog.getAttribute('cart-summary-sticky') !== 'false') {
        dialog.setAttribute('cart-summary-sticky', 'false');
      }
      return;
    }

    const drawerHeight = content.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    const sticky = ratio > this.#summaryThreshold ? 'false' : 'true';
    if (dialog.getAttribute('cart-summary-sticky') !== sticky) {
      dialog.setAttribute('cart-summary-sticky', sticky);
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
